import React, { useState, useCallback } from 'react';
import { api } from '@/services/api';

interface ImageUploadProps {
  eventId: string;
  onUpload: (fileUrl: string) => void;
  label?: string;
  accept?: string;
}

export function ImageUpload({
  eventId,
  onUpload,
  label = 'Upload Image',
  accept = 'image/*',
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) return;

      setUploading(true);
      setProgress(0);

      try {
        const { upload_url, file_url } = await api.getUploadUrl(
          eventId,
          file.name,
          file.type
        );

        setProgress(30);
        await api.uploadFile(upload_url, file);
        setProgress(100);

        onUpload(file_url);
      } catch (err) {
        console.error('Upload failed:', err);
      } finally {
        setTimeout(() => {
          setUploading(false);
          setProgress(0);
        }, 500);
      }
    },
    [eventId, onUpload]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
        isDragging
          ? 'border-violet-500 bg-violet-50'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      {uploading ? (
        <div className="space-y-3">
          <div className="animate-spin w-8 h-8 mx-auto border-b-2 border-violet-500 rounded-full" />
          <p className="text-sm text-gray-600">Uploading...</p>
          <div className="w-48 mx-auto bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-violet-500 h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-gray-600">{label}</p>
            <p className="text-xs text-gray-400 mt-1">
              Drag and drop or{' '}
              <label className="text-violet-600 hover:text-violet-700 cursor-pointer font-medium">
                browse
                <input
                  type="file"
                  accept={accept}
                  onChange={handleInputChange}
                  className="sr-only"
                />
              </label>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
